import React, { useEffect, useRef } from 'react'
import { icon } from '@fortawesome/fontawesome-svg-core'
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons'
import { loadScript } from '@paypal/paypal-js'

import InvalidForm from 'src/components/InvalidForm'
import cspNonce from 'src/utils/cspNonce'
import paymentMethodLabel from '../paymentMethodLabel'
import { genericError } from './useRecord'

const Summary = ({
  i18n, paypalConfig, donation, paypalProcessor, paymentOption, dismissSummary,
  validation, partnerItem, venmo,
}) => {
  const button = useRef()

  const createOrder = async paymentToken => {
    const params = {
      amount: donation.total,
      tip: donation.tip,
      currency: donation.currency.iso_code,
      partner_item_id: partnerItem.id,
      order_id: paypalProcessor.draftToken(),
    }

    // paymentToken can potentially be an object
    // Only include payment_token if it's a string
    if (typeof paymentToken == 'string')
      params.payment_token = paymentToken

    const response = await fetch(paypalConfig.create_path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params)
    })

    if (!response.ok) throw new Error('Failed to create PayPal order')
    const { id } = await response.json()
    return id
  }

  const createVaultSetupToken = async () => {
    const response = await fetch(paypalConfig.vault_setup_path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    })

    if (!response.ok) throw new Error('Failed to create PayPal vault setup token')
    const { id } = await response.json()
    return id
  }

  const onApproveVault = async data => {
    // The first month is charged below, so the panel is covered from here.
    // process() clears it on its own failures; this clears it on the vault
    // token's or the charge's.
    paypalProcessor.startCharge()

    let result, orderId
    try {
      // Handle vault setup approval
      const response = await fetch(paypalConfig.vault_approve_path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ vaultSetupToken: data.vaultSetupToken })
      })

      if (!response.ok) throw new Error('Failed to approve PayPal vault')
      result = await response.json()

      // Create PayPal order
      orderId = await createOrder(result.id)
    } catch (error) {
      console.error('PayPal error:', error)
      return paypalProcessor.failed(genericError(i18n))
    }

    // Process the payment and save the payment method
    return paypalProcessor.process({
      orderID: orderId,
      paymentToken: result.id,
      customerId: result.customer?.id,
    })
  }

  const clearButton = () => {
    while (button.current.firstChild)
      button.current.removeChild(button.current.firstChild)
  }

  const insertSpinner = () => {
    const spinner = icon(faCircleNotch, { classes: 'fa-2x fa-spin' })
    button.current.insertAdjacentHTML(
      'beforeend',
      `<div class="text-center" style="color: #0079c1;">${spinner.html}</div>`
    )
  }

  useEffect(() => {
    let buttons

    clearButton()
    insertSpinner()

    const vaultProps = paypalProcessor.idToken
      ? { 'data-user-id-token': paypalProcessor.idToken }
      : {}

    // 'enable-funding' is unconditional to match useVenmo's options — a
    // differing set injects a second script that overwrites window.paypal.
    // fundingSource below still pins which button renders.
    loadScript({
      'client-id': paypalConfig.client_id,
      'data-csp-nonce': cspNonce(),
      'enable-funding': 'venmo',
      currency: donation.currency.iso_code,
      ...vaultProps,
      ...venmo.sandboxProps,
    }).then(paypal => {
      clearButton()

      const onError = error => {
        console.error('PayPal error:', error)
        paypalProcessor.failed(genericError(i18n))
      }

      if (donation.state.subscription == 'one_time') {
        // Regular one-time payment
        buttons = paypal.Buttons({
          fundingSource: paypal.FUNDING[paymentOption.toUpperCase()],
          createOrder,
          onApprove: paypalProcessor.process,
          onError,
        })
        buttons.render(button.current)
      } else {
        // Subscription payments
        buttons = paypal.Buttons({
          fundingSource: paypal.FUNDING[paymentOption.toUpperCase()],
          createVaultSetupToken,
          onApprove: onApproveVault,
          onError,
        })
        buttons.render(button.current)
      }
    })
    .catch(error => {
      console.error('Failed to load PayPal JS SDK script', error)
      clearButton() // or the spinner runs forever
      paypalProcessor.failed({
        base: [ i18n.t('payment_methods.unavailable', {
          method: paymentMethodLabel(paymentOption, i18n),
        }) ],
      })
    })

    return () => buttons?.close()
  }, [])

  return (
    <div className="bg-white p-3 p-sm-4 rounded-4 shadow-lg">
      <div className="d-flex align-items-center justify-content-between">
        <h2 className="h5 modal-heading modal-heading-donation-summary">
          {i18n.t('donation_summary')}
        </h2>

        <div className="mb-2">
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={dismissSummary}
          >
            {i18n.t('cancel')}
          </button>
        </div>
      </div>

      <InvalidForm>{validation.errors?.base?.[0]}</InvalidForm>

      <div className="mx-n3 px-3 mx-sm-n4 px-sm-4 overflow-auto">
        <dl
          className="d-inline-grid column-gap-4"
          style={{ gridTemplateColumns: 'max-content auto' }}
        >
          {(!!Number(donation.tip) || donation.state.coverCosts) &&
            <>
              <dt>{i18n.t('donation')}</dt>
              <dd>
                {i18n.formatCurrency(donation.bigAmount, donation.currency)}
              </dd>

            {!!Number(donation.tip) &&
              <>
                <dt>{i18n.t('tip')}</dt>
                <dd>
                  {i18n.formatCurrency(donation.tip, donation.currency)}
                </dd>
              </>
            }

            {donation.state.coverCosts &&
              <>
                <dt>{i18n.t('fees')}</dt>
                <dd>
                  {i18n.formatCurrency(donation.fees, donation.currency)}
                </dd>
              </>
            }
            </>
          }

          <dt>{i18n.t('total')}</dt>
          <dd>
            {i18n.formatCurrency(donation.total, donation.currency)}
            {' '}
            {i18n.t(`subscription_labels.${donation.state.subscription}.submit`)}
          </dd>

          <dt>{i18n.t('name')}</dt>
          <dd>{donation.state.firstName} {donation.state.lastName}</dd>

          {donation.state.email &&
            <>
              <dt>{i18n.t('email')}</dt>
              <dd>{donation.state.email}</dd>
            </>
          }
        </dl>
      </div>

      <p>{i18n.t('paypal_summary', { method: paymentMethodLabel(paymentOption) })}</p>
      <div ref={button} className="d-grid" />
    </div>
  )
}

const SummaryWrapper = ({
  i18n, paypalConfig, donation, paypalProcessor, paymentOption, dismissSummary,
  validation, partnerItem, venmo,
}) => {
  switch (paymentOption) {
    case 'paypal':
    case 'venmo':
      return (
        <Summary
          i18n={i18n}
          paypalConfig={paypalConfig}
          donation={donation}
          paypalProcessor={paypalProcessor}
          paymentOption={paymentOption}
          dismissSummary={dismissSummary}
          validation={validation}
          partnerItem={partnerItem}
          venmo={venmo}
        />
      )
    default:
      return null
  }
}

export default SummaryWrapper
